import { useTranslation } from "react-i18next";
import { RefreshCwIcon } from "lucide-react";
import { formatRelativeTime, formatShortDate } from "@scandi/shared";
import { cn } from "@/lib/utils";

/**
 * "Data as of" line.
 *
 * Worth the space: numbers decide payouts, so a stalled sync has to be visible
 * rather than silently showing yesterday's standings as if they were live.
 */
export function DataFreshness({
  lastSyncedAt,
  dataThrough,
}: {
  lastSyncedAt: string | null;
  dataThrough: string | null;
}) {
  const { t } = useTranslation();

  if (!lastSyncedAt) {
    return (
      <p className="text-muted-foreground text-center text-xs">
        {t("range.waitingFirstSync")}
      </p>
    );
  }

  const syncedAt = new Date(lastSyncedAt);
  const elapsed = Date.now() - syncedAt.getTime();
  const ageMinutes = elapsed / 60_000;
  // The scheduler runs hourly, so anything past three hours means runs are failing.
  const stale = ageMinutes > 180;
  const age = elapsed < 60_000 ? t("common.justNow") : formatRelativeTime(elapsed);

  return (
    <p
      className={cn(
        "flex items-center justify-center gap-1.5 text-center text-xs",
        stale ? "text-warning" : "text-muted-foreground",
      )}
    >
      <RefreshCwIcon className="size-3" />
      {t("range.updated", { age })}
      {dataThrough && ` · ${t("range.dataThrough", { date: formatShortDate(dataThrough) })}`}
    </p>
  );
}
